import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function DeleteWorkspaceBlock() {
  const { activeWorkspaceId, activeWorkspace, user, refresh } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const isOwner = activeWorkspace?.role === "owner" || activeWorkspace?.owner_id === user?.id;
  const name = activeWorkspace?.name || "this workspace";

  if (!isOwner) {
    return (
      <div className="text-xs text-foreground/60 border-t border-red-500/20 pt-4" data-testid="delete-ws-not-owner">
        Only the workspace owner can delete this workspace.
      </div>
    );
  }

  const deleteWs = async () => {
    if (confirmText !== name) { toast.error(`Type exactly: ${name}`); return; }
    if (!window.confirm(`Final warning — this will permanently erase EVERY project, lead, invoice, file and member of "${name}". Continue?`)) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}?confirm=DELETE`);
      toast.success("Workspace deleted");
      await refresh?.();
      window.location.href = "/app";
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete workspace"));
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-red-500/30 pt-4 space-y-3" data-testid="delete-ws-block">
      <div className="flex items-start gap-2">
        <Trash2 className="w-4 h-4 text-red-500 mt-0.5" />
        <div>
          <div className="font-semibold text-sm text-red-600">Delete this workspace</div>
          <p className="text-xs text-foreground/70 mt-1 max-w-xl">
            Permanently removes <strong>{name}</strong> and every project, client, lead, invoice, file, calendar, employee record,
            credential and member. Other workspaces you belong to are unaffected.
          </p>
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-foreground/60 mb-1 block">
          Type the workspace name <code className="font-mono">{name}</code> to confirm
        </Label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="h-10 font-mono"
          placeholder={name}
          data-testid="delete-ws-confirm-input"
        />
      </div>
      <Button
        type="button"
        variant="destructive"
        className="rounded-none"
        disabled={busy || confirmText !== name}
        onClick={deleteWs}
        data-testid="delete-ws-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" /> {busy ? "Deleting…" : "Delete workspace permanently"}
      </Button>
    </div>
  );
}
